import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import get from 'lodash/get';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../bootstrap/Modal';
import AttendanceNotice from './components/AttendanceNotice';
import { useMst } from '../../models';
import { colors } from '../../helpers/constant';
import showNotification from '../../components/extras/showNotification';
import ButtonClockTime from './components/ButtonClockTime';
import WorkInfo from './components/WorkInfo';
import Button, { ButtonGroup } from '../bootstrap/Button';
import { getPersonDisplayName } from 'helpers/personNameI18n';
import Txt from 'components/common/ui/Txt';
import useRealtimeClock from 'hooks/useRealtimeClock';
import WorkGauge from './components/WorkGauge';
import ConfirmModal from 'components/common/ConfirmModal';

/******************************************************************
 * 파일명 : frontend2\src\components\Attend\AttendanceHome.js
 * 메뉴 : 사용자모드 > PC 출퇴근 타각 팝업
 * 설명 : Mobile에서는 해당 팝업 사용 하지 않고 AttendancePunch 사용!!
 ******************************************************************/
const EMPTY_TIME = '--:--:--';

const AttendanceHome = observer(({ setIsOpen, isOpen }) => {
  const { t } = useTranslation();
  const { attend: attendStore, user } = useMst();
  const { formattedTime } = useRealtimeClock();

  const [showPopupNotice, setShowPopupNotice] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [isButtonLocked, setIsButtonLocked] = useState(false);

  const { status, startTime2, endTime, endTime2, workStartTime, workEndTime } = get(attendStore, 'datas.info', {});
  const isAuthorizedIp = get(attendStore, 'isAuthorizedIp', false);

  // 헤더용: 시간대별 인사
  const hour = moment().hour();
  const greetingKey = hour < 12 ? 'userDashboard.greetingMorning2' : hour < 18 ? 'userDashboard.greetingAfternoon2' : 'userDashboard.greetingEvening2';

  // 'HH:mm' 형태의 예정시각을 'HH:mm:ss' 로 통일 (디자인: 18:00:00)
  const formatPlannedTime = (value) => {
    if (!value) return EMPTY_TIME;
    return /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
  };

  // 빈값(--:--:--)은 숫자보다 글자폭이 좁아 박스가 줄어듦 → 보이지 않는 00:00:00 을 깔아 실제 시간과 동일 폭 확보
  const TimeValue = ({ value }) => {
    if (value !== EMPTY_TIME) return <Txt variant='num_md'>{value}</Txt>;
    return (
      <Txt variant='num_md' className='time_placeholder'>
        <span aria-hidden className='sizer'>00:00:00</span>
        <span className='value'>{value}</span>
      </Txt>
    );
  };

  // 출근 박스: 출근 전이면 실시간 시계, 출근 후면 실제 출근시각 (기존 TimeDisplay 로직)
  const clockIn = startTime2
    ? { label: t('attendance.checkinDone'), value: moment(startTime2).format('HH:mm:ss') }
    : { label: t('attendance.checkin'), value: formattedTime };
  // 퇴근 박스: 퇴근 완료면 실제 퇴근시각, 아니면 근무 예정 종료시각(workEndTime)
  const clockOut = endTime2
    ? { label: t('attendance.checkoutDone'), value: moment(endTime2).format('HH:mm:ss') }
    : { label: t('attendance.checkoutPlanned'), value: formatPlannedTime(workEndTime) };

  const loadAttendStatus = () => {
    switch (attendStore?.get?.status) {
      case 1:
        attendStore.setType({
          mainButtonTitle: 'attendance.checkin',
          mainButtonSubTitle: 'attendance.beforeGoToWorkBtn',
          mainButtonWorkTime: `${attendStore.get.mainButtonWorkTime}`,
          mainButtonWorkTimeColor: '#FFF065',
          mainButtonColors: [colors.primaryDark, colors.primaryDark],
          startTime: '',
          endTime: '',
          subButtonStatus: 'S',
          mainButtonBoolean: false,
          mainButtonSubBoolean: attendStore?.get?.datas?.info?.startTime2 ? false : true,
          subButtonText: 'attendance.restartCheckout',
          subButtonIcon: 'ArrowLeftRight',
          subButtonBgColor: colors.white,
          subButtonColor: colors.grey,
        });
        break;
      case 2:
        attendStore.setType({
          mainButtonTitle: 'attendance.checkout',
          mainButtonSubTitle: 'attendance.workingBtn',
          mainButtonWorkTime: `${
            attendStore?.get?.datas?.info?.startTime2
              ? moment().valueOf() / 1000 - moment(attendStore.get.datas.info.startTime2).valueOf() / 1000
              : 0
          }`,
          mainButtonWorkTimeColor: colors.white,
          mainButtonColors: [colors.primary, colors.primary],
          startTime: attendStore.get.datas.info.startTime2,
          endTime: attendStore.get.datas.info.endTime2,
          subButtonStatus: 'W',
          mainButtonBoolean: false,
          mainButtonSubBoolean: false,
          subButtonText: 'attendance.breakStart',
          subButtonIcon: 'ri-play-fill',
          subButtonBgColor: colors.primaryDark,
          subButtonColor: colors.white,
        });
        break;
      case 3:
        attendStore.setType({
          mainButtonTitle: '휴식',
          mainButtonSubTitle: 'attendance.onBreak',
          mainButtonWorkTime: `${attendStore.get.mainButtonWorkTime}`,
          mainButtonWorkTimeColor: colors.white,
          mainButtonColors: [colors.primary, colors.primary],
          startTime: attendStore.get.datas.info.startTime2,
          endTime: attendStore.get.datas.info.endTime2,
          subButtonStatus: 'W',
          mainButtonBoolean: true,
          mainButtonSubBoolean: false,
          subButtonText: 'attendance.breakEnd',
          subButtonIcon: 'ri-stop-fill',
          subButtonBgColor: colors.secondary,
          subButtonColor: colors.white,
        });
        break;
      case 4: {
        const mainButtonWorkTime =
          moment(attendStore.get.datas.info.endTime2).valueOf() / 1000 -
          moment(attendStore.get.datas.info.startTime2).valueOf() / 1000;
        attendStore.setType({
          mainButtonTitle: 'attendance.offWork',
          mainButtonSubTitle: 'attendance.offWorkBtn',
          mainButtonWorkTime: `${mainButtonWorkTime}`,
          mainButtonWorkTimeColor: colors.white,
          mainButtonColors: [colors.primary, colors.primary],
          startTime: attendStore.get.datas.info.startTime2,
          endTime: attendStore.get.datas.info.endTime2,
          subButtonStatus: 'H',
          mainButtonBoolean: true,
          mainButtonSubBoolean: false,
          subButtonText: 'attendance.restartCheckin',
          subButtonIcon: 'ArrowLeftRight',
          subButtonBgColor: colors.white,
          subButtonColor: colors.grey,
        });
        break;
      }
      case 8: {
        // 비근무일 — 출퇴근 모두 완료 시 비활성, 그 전까지 타각 가능
        const holidayDone = !!get(attendStore, 'datas.info.startTime2') && !!get(attendStore, 'datas.info.endTime2');
        attendStore.setType({
          mainButtonTitle: attendStore.get.datas.info.text,
          mainButtonSubTitle: holidayDone ? '' : 'attendance.beforeGoToWorkBtn',
          mainButtonWorkTimeColor: '#FFF065',
          mainButtonColors: holidayDone
            ? [colors.white, colors.midiumGrey]
            : [colors.primaryDark, colors.primaryDark],
          mainButtonWorkTime: '',
          startTime: get(attendStore, 'datas.info.startTime2', ''),
          endTime: get(attendStore, 'datas.info.endTime2', ''),
          subButtonStatus: 'S',
          mainButtonBoolean: holidayDone,
          mainButtonSubBoolean: true,
          subButtonText: 'attendance.restartCheckin',
          subButtonIcon: 'ri-arrow-left-right-line',
          subButtonBgColor: colors.white,
          subButtonColor: colors.grey,
        });
        break;
      }
      case 99999: // 중복 접속
 
        /* props.navigation.reset({
          routes: [{name: 'CompanyList', params: {hideHeader: true}}],
        }); */
        // navigate('CompanyList')
        // Clipboard.setString(store.Config.uuid)
        // PINATModal({
        //   type: 'confrim',
        //   position: 'center',
        //   title:  t('안내'),
        //   message: t('중복 로그인이 감지 되었습니다', {device: store.Config.uuid}),
        //   delay: 0,
        //   buttons: [
        //     {
        //       type: 'cancel',
        //       label: t('확인'),
        //     },
        //   ]
        // })        
        break;
    }
  };

  const handleCheckinCheckout = () => {
    if (isButtonLocked) return;
    // 출근+퇴근 모두 완료(status 4)이면 다음날까지 출근 타각 불가
    if (attendStore?.get?.status === 4) return;
    const status = attendStore?.get?.status;
    const holidayDone = !!get(attendStore, 'datas.info.startTime2') && !!get(attendStore, 'datas.info.endTime2');
    // status 8(휴일)은 출퇴근 모두 완료 전까지 타각 허용
    if ([1, 2, 4].includes(status) || (status === 8 && !holidayDone)) {
      // 미인가 IP 경고
      if (!isAuthorizedIp) {
        showNotification(t('common.label.alert'), t('attendance.restrictIpWarning'), 'warning');
        return;
      }

      const nowMin = moment().format('HH:mm');
      const startMin = startTime2 ? moment(startTime2).format('HH:mm') : null;
      const endMin = endTime2 ? moment(endTime2).format('HH:mm') : null;
      if (nowMin === startMin || nowMin === endMin) {
        showNotification(t('common.label.alert'), t('attendance.duplicateMinuteWarning'), 'danger');
        return;
      }
      setIsButtonLocked(true);
      attendStore.requestAttend({ showNotification, t }, () => {
        setShowAlert(true);
        loadAttendStatus();
      }).finally(() => {
        setIsButtonLocked(false);
      });
    }
  };

  const goWorkButton = () => {
    attendStore.setStatus(1);
    attendStore.setWorkType(['GPS']);
  };

  // 근무지 명(현재 위치와 계산) + 오늘 출퇴근 정보 조회 + 위치 권한 확인
  useEffect(() => {
    const init = async () => {
      // 헤더 등에서 이미 불러왔으면 생략
      if (!get(attendStore, 'locationList', []).length) {
        await attendStore?.getLocationList();
      }
      await attendStore?.getAttendTodayInfo();
      attendStore?.checkGeolocationPermission();
      attendStore?.getUserLocation();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAttendStatus();
  }, [attendStore?.get?.status]);

  useEffect(() => {
    attendStore.setIsTimeRestarting(Boolean(endTime));
  }, [endTime]);

  useEffect(() => {
    attendStore?.checkAuthorizedIp();
  }, []);

  ////////////////////////////////////////////////////////////////////////////////////
	//------------------------------------- 화면 -------------------------------------//
  return (
    <>
      <Modal
          isStaticBackdrop
          isCentered
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          modalClassNameProps='attendance_modal'
        >
          <ModalHeader setIsOpen={setIsOpen}/>
          <ModalBody>
            <div className='top_area'>
              <Txt variant='h_page' className='greeting'>
                <Trans
                  i18nKey={greetingKey}
                  values={{ name: getPersonDisplayName(user.me) }}
                  components={{ br: <br /> }}
                />
              </Txt>
              <Txt variant='h_card' color='gray'>{moment().format(t('attendance.timeFormat'))}</Txt>
            </div>
            <div className='middle_area'>
              <WorkGauge
                startTime={startTime2}
                endTime={endTime2}
                scheduleStart={workStartTime}
                scheduleEnd={workEndTime}
              />
              <ul className='time_box'>
                <li className='work_on'>
                  <Txt color='gray'>{clockIn.label}</Txt>
                  <TimeValue value={clockIn.value} />
                </li>
                <li className='work_off'>
                  <Txt color='gray'>{clockOut.label}</Txt>
                  <TimeValue value={clockOut.value} />
                </li>
              </ul>
              {/* <TimeCounting setShowPopupNotice={setShowPopupNotice} /> */}
            </div>
            <div className='bottom_area'>
              { status && <WorkInfo /> }
              <ButtonClockTime
                handleCheckinCheckout={handleCheckinCheckout}
                isButtonLocked={isButtonLocked}
              />
            </div>

          </ModalBody>
      </Modal>

      {showPopupNotice && (
        <AttendanceNotice
          isOpen={showPopupNotice}
          setOpen={setShowPopupNotice}
          isHoliday={attendStore?.get?.datas.info.text === '휴일'}
          onOkButton={goWorkButton}
        />
      )}

      {showAlert &&
        <ConfirmModal
          isOpen={showAlert}
          setIsOpen={() => setShowAlert(false)}
          title={t(`attendance.${endTime2 ? 'checkout' : 'checkin' }`)}
          description={t('attendance.saveSuccess', { name: getPersonDisplayName(user.me), typeText: t(`attendance.${endTime2 ? 'checkout' : 'checkin' }`) })}
          hideCancel={true}
          confirmText={t('common.label.confirm')}
          onConfirm={() => setShowAlert(false)}
        />
      }
    </>
  );
});

export default React.memo(AttendanceHome);
